import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, Eye, EyeOff, Radio } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LiveStrip } from "@/pages/LoginStrip";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: false });
  const [code, setCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/painel"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const data = await login(form.email, form.password, form.remember, needs2fa ? code : null);
      if (data.requires_2fa) { setNeeds2fa(true); return; }
      toast.success(`Bem-vindo(a), ${data.user.name.split(" ")[0]}`);
      navigate(data.user.role === "admin" ? "/admin" : "/painel");
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#070A12] p-12 text-white grain">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(1200px 500px at 0% 100%, rgba(30,58,138,.55), transparent 60%), radial-gradient(600px 300px at 90% 10%, rgba(245,158,11,.18), transparent 60%)" }} />
        <LogoMark size={520} className="pointer-events-none absolute -right-24 top-1/2 -translate-y-1/2 opacity-[0.07]" />
        <div className="relative"><Logo size={48} /></div>
        <div className="relative max-w-xl">
          <div className="eyebrow !text-amber-400/90 mb-5">Investimentos · Trading · Mercados ao vivo</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Seu capital,<br />com <span className="text-amber-400">precisão</span> institucional.
          </h1>
          <p className="mt-6 text-base text-slate-300 max-w-md">Forex, criptomoedas, ações, índices e commodities em uma única plataforma — com gráficos profissionais e controle total do seu risco.</p>
          <div className="mt-8 flex flex-wrap gap-5 text-sm text-slate-300">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-400" />2FA e sessões controladas</span>
            <span className="flex items-center gap-2"><Radio className="h-4 w-4 text-emerald-400" />Cripto em tempo real</span>
            <span className="flex items-center gap-2"><Lock className="h-4 w-4 text-amber-400" />Senhas nunca salvas no navegador</span>
          </div>
        </div>
        <LiveStrip />
      </section>

      <section className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
        <div className="lg:hidden mb-10"><Logo size={48} /></div>
        <div className="w-full max-w-md fade-up">
          <LogoMark size={56} className="hidden lg:block mb-6 rounded-2xl" testid="login-form-mark" />
          <div className="eyebrow mb-3">Área do cliente</div>
          <h2 className="text-3xl font-bold tracking-tight">{needs2fa ? "Verificação em dois fatores" : "Entrar na sua conta"}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {needs2fa ? "Digite o código de 6 dígitos do seu aplicativo autenticador." : "As contas são criadas pela equipe TRADE REPUBLIC. Use o e-mail e a senha que você recebeu."}
          </p>
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {!needs2fa ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm">E-mail</Label>
                  <Input id="email" type="email" autoComplete="username" required className="h-12 text-base" data-testid="login-email-input"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="voce@email.com" />
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between"><Label htmlFor="pw" className="text-sm">Senha</Label>
                    <Link to="/esqueci-senha" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">Esqueci minha senha</Link></div>
                  <div className="relative">
                    <Input id="pw" type={show ? "text" : "password"} autoComplete="current-password" required className="h-12 pr-12 text-base" data-testid="login-password-input"
                      value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                    <button type="button" aria-label="Mostrar senha" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-muted-foreground" data-testid="toggle-password-visibility">
                      {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <label className="flex items-center gap-3 text-sm cursor-pointer">
                  <Checkbox checked={form.remember} onCheckedChange={(v) => setForm({ ...form, remember: !!v })} data-testid="login-remember-checkbox" />
                  Manter-me conectado por 30 dias neste dispositivo
                </label>
              </>
            ) : (
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="login-2fa-input" autoFocus>
                <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />)}</InputOTPGroup>
              </InputOTP>
            )}
            {error && <div className="rounded-lg border border-down/40 bg-down-soft px-4 py-3 text-sm text-down" role="alert" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="h-12 w-full text-base font-semibold rounded-xl" data-testid="login-submit-button">
              {busy ? "Verificando…" : needs2fa ? "Confirmar código" : "Entrar"}
            </Button>
            {needs2fa && <Button type="button" variant="ghost" className="w-full" data-testid="login-2fa-back" onClick={() => { setNeeds2fa(false); setCode(""); }}>Voltar</Button>}
          </form>
          <p className="mt-10 text-xs text-muted-foreground">
            Ao entrar você concorda com os <Link to="/legal/termos" className="underline">Termos de uso</Link> e a{" "}
            <Link to="/legal/privacidade" className="underline">Política de privacidade</Link>. Investimentos envolvem riscos.
          </p>
        </div>
      </section>
    </div>
  );
}
